import { useEffect, useMemo, useState } from 'react';
import AppModal from '../components/AppModal';
import api from '../services/api';
import { obtenerUsuarioSesion } from '../utils/sesion';

function estadoInicial() {
  return {
    Id: null,
    EmpresaId: '',
    Nombre: '',
    Tipo: 'Emisor',
    Activo: true
  };
}

function obtenerUsuarioActual() {
  return obtenerUsuarioSesion();
}

function PersonasPage() {
  const [personas, setPersonas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [form, setForm] = useState(estadoInicial());
  const [mensaje, setMensaje] = useState('');
  const [errores, setErrores] = useState([]);
  const [editando, setEditando] = useState(false);
  const [personaEstatusModal, setPersonaEstatusModal] = useState(null);

  const usuarioActual = obtenerUsuarioActual();
  const usuarioEsAdministrador =
    usuarioActual.Rol === 'Administrador' ||
    usuarioActual.rol === 'Administrador';

  const [filtros, setFiltros] = useState({
    texto: '',
    empresaId: '',
    tipo: '',
    estatus: 'activos'
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  async function cargarDatos() {
    try {
      const [personasRes, empresasRes] = await Promise.all([
        api.get('/personas'),
        api.get('/empresas')
      ]);

      setPersonas(personasRes.data);
      setEmpresas(empresasRes.data);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  function handleFiltro(e) {
    const { name, value } = e.target;

    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function limpiarFiltros() {
    setFiltros({
      texto: '',
      empresaId: '',
      tipo: '',
      estatus: 'activos'
    });
  }

  function validar() {
    const pendientes = [];

    if (!form.Nombre.trim()) {
      pendientes.push('Nombre es obligatorio.');
    }

    if (!form.Tipo) {
      pendientes.push('Tipo es obligatorio.');
    }

    return pendientes;
  }

  async function guardar(e) {
    e.preventDefault();

    setMensaje('');
    setErrores([]);

    const pendientes = validar();

    if (pendientes.length > 0) {
      setErrores(pendientes);
      setMensaje('Revisa los campos antes de guardar.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = {
      EmpresaId: form.EmpresaId ? Number(form.EmpresaId) : null,
      Nombre: form.Nombre.trim(),
      Tipo: form.Tipo
    };

    if (usuarioEsAdministrador) {
      payload.Activo = Boolean(form.Activo);
    }

    try {
      if (editando) {
        await api.put(`/personas/${form.Id}`, payload);
        setMensaje('Persona actualizada correctamente.');
      } else {
        await api.post('/personas', payload);
        setMensaje('Persona creada correctamente.');
      }

      setForm(estadoInicial());
      setEditando(false);
      await cargarDatos();

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function editarPersona(persona) {
    setForm({
      Id: persona.Id,
      EmpresaId: persona.EmpresaId || '',
      Nombre: persona.Nombre || '',
      Tipo: persona.Tipo || 'Emisor',
      Activo: Boolean(persona.Activo)
    });

    setEditando(true);
    setMensaje('');
    setErrores([]);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelarEdicion() {
    setForm(estadoInicial());
    setEditando(false);
    setMensaje('');
    setErrores([]);
  }

  function cambiarEstatus(persona) {
    setPersonaEstatusModal(persona);
  }

  async function confirmarCambioEstatus() {
    const persona = personaEstatusModal;

    if (!persona) return;

    setPersonaEstatusModal(null);

    try {
      await api.delete(`/personas/${persona.Id}`);
      setMensaje(
        `Persona ${persona.Activo ? 'desactivada' : 'activada'} correctamente.`
      );
      await cargarDatos();
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  const personasFiltradas = useMemo(() => {
    const texto = filtros.texto.trim().toLowerCase();

    return personas.filter((persona) => {
      const coincideTexto =
        !texto ||
        String(persona.Nombre || '').toLowerCase().includes(texto) ||
        String(persona.Empresa || '').toLowerCase().includes(texto) ||
        String(persona.Tipo || '').toLowerCase().includes(texto);

      const coincideEmpresa =
        !filtros.empresaId ||
        String(persona.EmpresaId || '') === String(filtros.empresaId);

      const coincideTipo =
        !filtros.tipo ||
        String(persona.Tipo || '') === filtros.tipo;

      const coincideEstatus =
        filtros.estatus === 'todos' ||
        (filtros.estatus === 'activos' && persona.Activo) ||
        (filtros.estatus === 'inactivos' && !persona.Activo);

      return coincideTexto &&
        coincideEmpresa &&
        coincideTipo &&
        coincideEstatus;
    });
  }, [personas, filtros]);

  return (
    <div className="page-container-normal">

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Personas</h1>

          <div className="text-muted">
            Administración de emisores y receptores
          </div>
        </div>
      </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {errores.length > 0 && (
        <div className="alert alert-warning">
          <div className="fw-bold mb-2">
            ⚠ Campos pendientes:
          </div>

          <ul className="mb-0">
            {errores.map((error, index) => (
              <li key={index}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={guardar}>
        <div className="card-box mb-3">
          <div className="personas-grid-form">

            <div>
              <label className="form-label">
                Nombre <span className="text-danger">*</span>
              </label>

              <input
                className="form-control"
                name="Nombre"
                value={form.Nombre}
                onChange={handleChange}
                placeholder="Nombre completo"
              />
            </div>

            <div>
              <label className="form-label">
                Empresa
              </label>

              <select
                className="form-select"
                name="EmpresaId"
                value={form.EmpresaId}
                onChange={handleChange}
              >
                <option value="">Sin empresa</option>

                {empresas.map((empresa) => (
                  <option key={empresa.Id} value={empresa.Id}>
                    {empresa.Nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Tipo <span className="text-danger">*</span>
              </label>

              <select
                className="form-select"
                name="Tipo"
                value={form.Tipo}
                onChange={handleChange}
              >
                <option value="Emisor">Emisor</option>
                <option value="Receptor">Receptor</option>
              </select>
            </div>

            <div className="personas-acciones">
              {usuarioEsAdministrador && (
                <div className="personas-check-activo">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="Activo"
                    checked={form.Activo}
                    onChange={handleChange}
                    id="ActivoPersona"
                  />

                  <label
                    className="form-check-label"
                    htmlFor="ActivoPersona"
                  >
                    Activo
                  </label>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-guardar-persona"
              >
                {editando ? 'Guardar cambios' : 'Crear persona'}
              </button>

              {editando && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-cancelar-persona"
                  onClick={cancelarEdicion}
                >
                  Cancelar
                </button>
              )}
            </div>

          </div>
        </div>
      </form>

      <div className="card-box mb-3">
        <div className="personas-grid-busqueda">

          <div>
            <label className="form-label">
              Buscar
            </label>

            <input
              className="form-control"
              name="texto"
              value={filtros.texto}
              onChange={handleFiltro}
              placeholder="Nombre, empresa o tipo"
            />
          </div>

          <div>
            <label className="form-label">
              Empresa
            </label>

            <select
              className="form-select"
              name="empresaId"
              value={filtros.empresaId}
              onChange={handleFiltro}
            >
              <option value="">Todas</option>

              {empresas.map((empresa) => (
                <option key={empresa.Id} value={empresa.Id}>
                  {empresa.Nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">
              Tipo
            </label>

            <select
              className="form-select"
              name="tipo"
              value={filtros.tipo}
              onChange={handleFiltro}
            >
              <option value="">Todos</option>
              <option value="Emisor">Emisor</option>
              <option value="Receptor">Receptor</option>
            </select>
          </div>

          <div className="personas-busqueda-final">
            <div>
              <label className="form-label">
                Estatus
              </label>

              <select
                className="form-select"
                name="estatus"
                value={filtros.estatus}
                onChange={handleFiltro}
              >
                <option value="activos">Activos</option>
                <option value="inactivos">Inactivos</option>
                <option value="todos">Todos</option>
              </select>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary btn-limpiar-personas"
              onClick={limpiarFiltros}
            >
              Limpiar
            </button>
          </div>

        </div>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Personas registradas</h5>

          <span className="badge bg-secondary">
            {personasFiltradas.length} registros
          </span>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Empresa</th>
                <th>Tipo</th>
                <th className="text-center">Estatus</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {personasFiltradas.map((persona) => (
                <tr key={persona.Id}>
                  <td>
                    <strong>{persona.Nombre}</strong>
                  </td>

                  <td>
                    {persona.Empresa || (
                      <span className="text-muted">Sin empresa</span>
                    )}
                  </td>

                  <td>{persona.Tipo}</td>

                  <td className="text-center">
                    {persona.Activo ? (
                      <span className="badge bg-success">Activo</span>
                    ) : (
                      <span className="badge bg-secondary">Inactivo</span>
                    )}
                  </td>

                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => editarPersona(persona)}
                      >
                        Editar
                      </button>

                      {usuarioEsAdministrador && (
                        <button
                          type="button"
                          className={
                            persona.Activo
                              ? 'btn btn-sm btn-outline-danger'
                              : 'btn btn-sm btn-outline-success'
                          }
                          onClick={() => cambiarEstatus(persona)}
                        >
                          {persona.Activo ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {personasFiltradas.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center text-muted py-4"
                  >
                    No hay personas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AppModal
        abierto={Boolean(personaEstatusModal)}
        titulo="Sistema Recibos"
        mensaje={
          personaEstatusModal
            ? `¿Deseas ${personaEstatusModal.Activo ? 'desactivar' : 'activar'} a ${personaEstatusModal.Nombre}?`
            : ''
        }
        variante={personaEstatusModal?.Activo ? 'danger' : 'warning'}
        textoCancelar="Cancelar"
        textoAceptar={personaEstatusModal?.Activo ? 'Desactivar' : 'Activar'}
        onCancelar={() => setPersonaEstatusModal(null)}
        onAceptar={confirmarCambioEstatus}
      />

    </div>
  );
}

export default PersonasPage;
